/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, of, switchMap, takeUntil } from "rxjs";

// PrimeNG imports
import { FormsModule } from "@angular/forms";
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Services
import {
  ProductGroup,
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { ProductGroupsService } from "../../services/product-groups.service";

// Models
import {
  IProductGroupFilterFormControls,
  IProductGroupFilterParams,
  IProductGroupListResponse,
  IProductGroupPageEvent,
  IProductGroupRowAction,
  IProductGroupSortEvent,
} from "../../interfaces/product-group.interface";

// Components
import { ProductGroupsFiltersComponent } from "../../components/product-groups-filters/product-groups-filters.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";
import { ProductGroupsTableComponent } from "../../components/product-groups-table/product-groups-table.component";

// Site interface
import { SiteFull } from "../../../../core/models/site.interface";

/**
 * Product Groups List Page Component
 * Main orchestrating component that manages filters, table, and actions
 */
@Component({
  selector: "app-product-groups-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    TooltipModule,
    ProductGroupsTableComponent,
    ProductGroupsFiltersComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./product-groups-list.component.html",
  styleUrl: "./product-groups-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class ProductGroupsListComponent implements OnInit, OnDestroy {
  // Dual mode inputs for web component/iframe support
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;

  private readonly elementRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();

  // Reactive state using signals
  readonly currentSiteId = signal<number | null>(null);
  private readonly currentSite = signal<SiteFull | null>(null);

  // Data signals
  private readonly productGroups = signal<IProductGroupListResponse | null>(
    null,
  );

  // UI state
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false;

  // Loading states
  private readonly isLoading = signal(false);
  private readonly isInitialLoading = signal(true);

  // Pagination & sort state
  private readonly currentPage = signal(1);
  private readonly currentPageSize = signal(20);
  private readonly currentSortField = signal("Name");
  private readonly currentSortOrder = signal<1 | -1>(1);

  // Form management
  filterForm!: FormGroup<IProductGroupFilterFormControls>;

  // Computed values
  readonly data = computed(() => this.productGroups());
  readonly loading = computed(() => this.isLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());
  readonly currentSiteData = computed(() => this.currentSite());

  // Track active filters state with a signal for proper reactivity
  private readonly activeFiltersState = signal(false);

  // Flag to skip duplicate API call after clearing filters
  private skipNextFilterChange = false;

  // Check if filters are active
  readonly hasActiveFilters = computed(() => this.activeFiltersState());

  constructor(
    private readonly productGroupsService: ProductGroupsService,
    private readonly sessionService: SessionService,
    private readonly qpilotService: QPilotService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly fb: FormBuilder,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Detect embedded mode from attribute (for web components)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");
    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get token from attribute in embedded mode
    if (this.embeddedMode) {
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      // Create inputToken from attribute if:
      // 1. We have a token string from attribute
      // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
      // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }
    }

    this.setupSiteSubscription();
    this.setupRefreshSubscription();
    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== Initialization ====================

  private initializeForm(): void {
    this.filterForm = new FormGroup<IProductGroupFilterFormControls>({
      search: new FormControl<string>("", { nonNullable: true }),
    });
  }

  private initializeResponsiveSidebar(): void {
    // Auto-collapse on mobile (matching scheduled-orders pattern)
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   * Uses ResizeObserver for efficient resize detection (matching scheduled-orders pattern)
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      // Auto-collapse on mobile if user hasn't manually set preference
      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      }
      // Auto-expand on desktop if user hasn't manually collapsed
      else if (!isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(false);
      }
      // If user manually toggled, respect their preference regardless of screen size

      this.cdr.markForCheck();
    });

    resizeObserver.observe(document.body);

    // Clean up observer on destroy
    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  private setupSiteSubscription(): void {
    let siteId: number | null = null;

    if (this.embeddedMode) {
      // Embedded mode: Get siteId from input or attribute
      const element = this.elementRef.nativeElement as HTMLElement;
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      if (siteIdString) {
        siteId = parseInt(siteIdString, 10);
      }
    } else {
      // Standard mode: Get site ID from parent route parameter
      siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
    }

    if (siteId && siteId > 0) {
      this.currentSiteId.set(siteId);

      // Fetch site data to get UtcOffset
      const accessToken = this.getAccessToken();
      if (accessToken) {
        this.fetchSiteDataAndInitialize(siteId, accessToken);
      } else {
        this.isInitialLoading.set(false);
        this.cdr.markForCheck();
      }
    } else {
      this.currentSiteId.set(null);
      this.isInitialLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  /**
   * Fetch site data and then load product groups
   */
  private fetchSiteDataAndInitialize(
    siteId: number,
    accessToken: QPilotAccessToken,
  ): void {
    this.qpilotService
      .getSiteFull(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site: SiteFull) => {
          this.currentSite.set(site);
          this.loadProductGroups();
          this.cdr.markForCheck();
        },
        error: () => {
          this.isInitialLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  // Note: Filter subscription is handled by ProductGroupsFiltersComponent
  // which emits filtersChanged event with debounce. This avoids duplicate API calls.

  private setupRefreshSubscription(): void {
    this.refresh$
      .pipe(
        switchMap(() => {
          const siteId = this.currentSiteId();
          const accessToken = this.getAccessToken();

          if (!siteId || !accessToken) {
            return of(null);
          }

          this.isLoading.set(true);
          this.cdr.markForCheck();

          const params: IProductGroupFilterParams = {
            search: this.filterForm.controls.search.value || undefined,
            page: this.currentPage(),
            pageSize: this.currentPageSize(),
            sortField: this.currentSortField(),
            sortOrder: this.currentSortOrder(),
          };

          return this.productGroupsService.getProductGroups(
            siteId,
            accessToken,
            params,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.productGroups.set(response);
          }
          this.isLoading.set(false);
          this.isInitialLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.isInitialLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  // ==================== Data Loading ====================

  private loadProductGroups(): void {
    this.refresh$.next();
  }

  // ==================== UI Actions ====================

  toggleSidebar(): void {
    this.userManuallyCollapsed = true;
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }

  // ==================== Filter Handlers ====================

  onFiltersChanged(filters: IProductGroupFilterParams): void {
    // Skip if we just handled this in onClearFilters (prevents duplicate API call)
    if (this.skipNextFilterChange) {
      this.skipNextFilterChange = false;
      return;
    }
    // Update active filters state for UI reactivity
    this.activeFiltersState.set(!!filters.search?.trim());
    this.currentPage.set(1);
    this.loadProductGroups();
  }

  onClearFilters(): void {
    // Update active filters state immediately for UI reactivity
    this.activeFiltersState.set(false);
    // Set flag to skip the next filtersChanged emission (from debounced valueChanges)
    this.skipNextFilterChange = true;
    // Let valueChanges fire normally so distinctUntilChanged sees the "" value
    this.filterForm.patchValue({ search: "" });
    this.currentPage.set(1);
    this.loadProductGroups();
    this.cdr.markForCheck();
  }

  // ==================== Sort & Page Handlers ====================

  onSortChanged(event: IProductGroupSortEvent): void {
    this.currentSortField.set(event.field);
    this.currentSortOrder.set(event.order);
    this.loadProductGroups();
  }

  onPageChanged(event: IProductGroupPageEvent): void {
    this.currentPage.set(event.page);
    this.currentPageSize.set(event.rows);
    this.loadProductGroups();
  }

  // ==================== Row Actions ====================

  onRowAction(event: IProductGroupRowAction): void {
    switch (event.action) {
      case "edit":
        this.navigateToEdit(event.group);
        break;
      case "delete":
        this.confirmDelete(event.group);
        break;
      case "viewProduct":
        if (event.productId) {
          this.navigateToProduct(event.productId);
        }
        break;
      case "searchProduct":
        if (event.productSearch) {
          this.navigateToProductSearch(event.productSearch);
        }
        break;
    }
  }

  private navigateToProduct(productId: string): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "product-groups-list",
          action: "viewProduct",
          productId: productId,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        siteId,
        "products",
        productId,
      ]);
    }
  }

  private navigateToProductSearch(search: string): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "product-groups-list",
          action: "searchProducts",
          searchTerm: search,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(["/widgets/dashboard", siteId, "products"], {
        queryParams: { search },
      });
    }
  }

  navigateToCreate(): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "product-groups-list",
          action: "create",
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(["create"], { relativeTo: this.route });
    }
  }

  private navigateToEdit(group: ProductGroup): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "product-groups-list",
          action: "edit",
          productGroupId: group.ProductGroupId,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([group.ProductGroupId, "edit"], {
        relativeTo: this.route,
      });
    }
  }

  private confirmDelete(group: ProductGroup): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${group.Name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => this.deleteGroup(group),
    });
  }

  private deleteGroup(group: ProductGroup): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    this.productGroupsService
      .deleteProductGroup(siteId, group.ProductGroupId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadProductGroups();
        },
      });
  }
}
